import React from 'react'
import Path from 'svg-path-generator'

interface Props extends React.HtmlHTMLAttributes<SVGSVGElement> {
    barBackgroundColor: string
    barForegroundColor: string
    axisColor: string
    items: BarChartItem[]
    yAxisIsPercentage?: boolean
}

export type BarChartItem = {
    label: string
    backgroundValue: number
    foregroundValue: number
}

const BarChart: React.FC<Props> = ({items, barForegroundColor, barBackgroundColor, axisColor, yAxisIsPercentage, ...props}) => {

    const fontSize = 17
    const barStrokeWidth = 10

    const yAxisLabelWidth = 45
    const yAxisLabelMargin = 10
    const yAxisTopMargin = 10
    const yAxisStartX = (yAxisLabelWidth + yAxisLabelMargin)
    const yAxisHeight = 200
    const xAxisLabelHeight = 30
    const xAxisLabelWidth = 30
    const xAxisLabelMarginX = 15
    const xAxisLabelMarginY = 5
    const xAxisY = (yAxisTopMargin + yAxisHeight)

    const width = (yAxisStartX + (xAxisLabelWidth * items.length) + (xAxisLabelMarginX * (items.length - 1)))
    const height = (xAxisLabelHeight + yAxisHeight + yAxisTopMargin)

    const yAxisSteps = getYAxisStep(items, yAxisIsPercentage)
    const yAxisMaxValue = (Math.ceil(yAxisSteps.numberOfSteps) * yAxisSteps.stepHeight)

    let yAxisLabels = new Array<number>()

    for (let y = 0; y <= yAxisMaxValue; y += yAxisSteps.stepHeight) {
        yAxisLabels.push(y)
    }

    return <svg viewBox={`0 0 ${width} ${height}`} {...props}>
        
        { yAxisLabels.map(value => {
                const y = (yAxisTopMargin + (yAxisHeight * (1 - (value / yAxisMaxValue))))
                const yText = yAxisIsPercentage ? (value + "%") : value
                return <React.Fragment key={value}>
                    <text x={yAxisLabelWidth} y={y} dominantBaseline="central" textAnchor="end" fill={axisColor} fontSize={fontSize}>{yText}</text>
                    <line x1={yAxisStartX} y1={y} x2={width} y2={y} stroke={axisColor}/>
                </React.Fragment>
            }) }
        
        {items.map((item, i) => {

            const x = (yAxisStartX + ((i + 0.5) * xAxisLabelWidth) + (i * xAxisLabelMarginX))

            const backgroundHeight = ((valueOrZero(item.backgroundValue) / yAxisMaxValue) * yAxisHeight)
            const foregroundHeight = ((valueOrZero(item.foregroundValue) / yAxisMaxValue) * yAxisHeight)

            return <React.Fragment key={i}>
                <text x={x} y={xAxisY + xAxisLabelMarginY} dominantBaseline="hanging" textAnchor="middle" fill={axisColor} fontSize={fontSize}>{item.label}</text>
                {verticalLineWithCoordinates({midX: x, bottomY: xAxisY, topY: (xAxisY - backgroundHeight), width: barStrokeWidth, color: barBackgroundColor})}
                {verticalLineWithCoordinates({midX: x, bottomY: xAxisY, topY: (xAxisY - foregroundHeight), width: barStrokeWidth, color: barForegroundColor})}
            </React.Fragment>
        })}

    </svg>
}

export default BarChart

// The server sends null for a month without inspections
const valueOrZero = (value: number | null | undefined) => (typeof value === "number" && isFinite(value)) ? value : 0

const getYAxisStep = (items: BarChartItem[], yAxisIsPercentage?: boolean) => {

    const maxNumberOfSteps = 5
    const possibleYAxisSteps = [1,2,5,10,20,50,100]

    const maxValue = Math.max(0, ...items.map(i => valueOrZero(i.backgroundValue)),
                                 ...items.map(i => valueOrZero(i.foregroundValue)))

    // With no data the axis would end at 0 and every position would divide by it
    if (maxValue === 0) {
        return yAxisIsPercentage ? { numberOfSteps: 5, stepHeight: 20 } : { numberOfSteps: 1, stepHeight: 1 }
    }

    const numberOfSteps = possibleYAxisSteps.map(s => {return { numberOfSteps: (maxValue / s), stepHeight: s}})
    const validSteps = numberOfSteps.filter(({numberOfSteps}) => numberOfSteps <= maxNumberOfSteps)

    return validSteps[0]
}

interface VerticalLineProps {
    midX: number
    bottomY: number
    topY: number
    width: number
    color: string
}

const verticalLineWithCoordinates = (props: VerticalLineProps) => {

    const { midX, bottomY, topY, width, color } = props

    if (topY === bottomY) {
        return <></>
    }

    const arcRadius = (width / 2)
    const leftX = (midX - arcRadius)
    const rightX = (midX + arcRadius)
    const bottomArcY = (topY + arcRadius)

    const path = Path().moveTo(leftX, bottomY)
                       .lineTo(leftX, bottomArcY)
                       .ellipticalArc(arcRadius, arcRadius, 0, 1, 1, rightX, bottomArcY)
                       .lineTo(rightX, bottomY)
                       .close()

    return <path d={path} fill={color}/>
}